import {
  createRealtimeClient,
  inboxStream,
  inventoryStream,
  type RealtimeClient,
  type TokenProvider,
} from "@tcg/realtime";

export interface AdminRealtimeOptions {
  /** Gateway root without the version (`/api`) — the same as every admin API call. */
  baseUrl: string;
  /** Tests inject the fake backend's fetch. */
  fetch?: typeof fetch;
  /** Staff bearer; `force` = the last one was refused → refresh first. */
  getToken: TokenProvider;
  /** Language of the to-do texts the inbox stream sends (ja/th/en). */
  locale: () => string | null;
  /** Tests: no document/window listeners. */
  watchPage?: boolean;
  /** Tests: faster reconnects. */
  minRetryMs?: number;
}

/**
 * The tab's one realtime client (R21, ADR-0010, T11-52): the hub (`/api/v1/realtime/stream` —
 * every `staff:<area>` topic on one connection) plus the two per-service streams of ADR-0009 the
 * admin reads — notification-service's to-do badge (`inbox`) and inventory-service's availability
 * (`inventory.changed:<variantId>`), both behind the same `subscribe()`.
 */
export function createAdminRealtime(options: AdminRealtimeOptions): RealtimeClient {
  const root = options.baseUrl.replace(/\/+$/, "");
  return createRealtimeClient({
    baseUrl: `${root}/v1/realtime`,
    getToken: options.getToken,
    // staff always send a bearer → fetch transport (EventSource cannot set Authorization)
    transport: "fetch",
    ...(options.fetch ? { fetch: options.fetch } : {}),
    ...(options.watchPage !== undefined ? { watchPage: options.watchPage } : {}),
    ...(options.minRetryMs !== undefined ? { minRetryMs: options.minRetryMs } : {}),
    streams: [
      inboxStream({ path: `${root}/v1/admin/inbox/stream`, locale: options.locale }),
      inventoryStream({ path: `${root}/v1/admin/inventory/stream` }),
    ],
  });
}
